import { useState } from "react";

import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { roleLabel } from "@/lib/roleLabels";
import { CaretDownIcon, CheckCircleIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";

/**
 * Picks several people, from a control that looks and behaves like the
 * single-owner <Select> next to it.
 *
 * It used to render its search box and its whole checklist inline, permanently
 * open — which on the initiative pages meant a 40-row scrolling list sitting in
 * the middle of the form, pushing everything below it down and reading as
 * something that had failed to close. A plain <Select> cannot hold multiple
 * values, so this is a Popover with the same trigger shape: closed, it shows
 * who is selected; open, it shows the list.
 *
 * The popover deliberately stays open while ticking, because the whole point is
 * choosing more than one person.
 *
 * `users` are api.js-shaped rows (_id/name/email/role); `value` is an array of
 * user ids and `onChange` receives the next array.
 */
export default function PeopleMultiSelect({
  users = [],
  value = [],
  onChange,
  placeholder = "Search people…",
  triggerPlaceholder = "Select people…",
  emptyMessage = "No accounts to choose from.",
  testId = "people-multi-select",
  maxHeight = "max-h-56",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const matches = q
    ? users.filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q))
    : users;

  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  const nameOf = (id) => {
    const u = users.find((x) => x._id === id);
    return u ? (u.name || u.email) : "Unknown";
  };

  // Names, not a count: "Alex Chacko, Sandeep Naruka" answers the question the
  // closed control is asked. Long lists fall back to a count so the trigger
  // cannot grow past its row.
  const summary = value.length === 0
    ? triggerPlaceholder
    : value.length <= 3
      ? value.map(nameOf).join(", ")
      : `${value.length} people selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          aria-expanded={open}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-sm border border-input bg-white px-3 text-sm shadow-sm transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className={`truncate text-left ${value.length === 0 ? "text-muted-foreground" : ""}`}>
            {summary}
          </span>
          <CaretDownIcon size={12} className="shrink-0 text-zinc-500" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        // Matches the trigger's width, the way a <Select> menu does, instead of
        // the popover's default content-driven width.
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        {users.length > 0 && (
          <div className="relative border-b border-border p-2">
            <MagnifyingGlassIcon size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              data-testid={`${testId}-search`}
              className="h-8 rounded-sm pl-7 text-sm"
            />
          </div>
        )}

        <div className={`${maxHeight} overflow-y-auto divide-y divide-border`} data-testid={testId}>
          {users.length === 0 && (
            <div className="px-3 py-4 text-xs text-zinc-500" data-testid={`${testId}-empty`}>{emptyMessage}</div>
          )}
          {users.length > 0 && matches.length === 0 && (
            <div className="px-3 py-4 text-xs text-zinc-500" data-testid={`${testId}-no-match`}>
              Nobody matches “{query.trim()}”.
            </div>
          )}
          {matches.map((u) => {
            const checked = value.includes(u._id);
            return (
              <button
                type="button"
                key={u._id}
                onClick={() => toggle(u._id)}
                data-testid={`${testId}-option-${u._id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${checked ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
              >
                <span className="min-w-0 truncate">
                  {u.name || u.email}
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1.5">{roleLabel(u.role)}</span>
                </span>
                {checked && <CheckCircleIcon size={16} weight="fill" color="#0176D3" className="shrink-0" />}
              </button>
            );
          })}
        </div>

        {value.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2">
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              {value.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
